import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowRight, CheckCircle2, Cpu, Mail, Radar, RefreshCw, Shield, ShieldCheck, Sparkles } from 'lucide-react';

import BackButton from '../components/BackButton';
import Logo from '../components/Logo';
import PageTitle from '../components/PageTitle';
import { getApiErrorMessage } from '../utils/errorMessages';

const productShowcase = [
  { icon: Cpu, labelKey: 'topbar.reverse_model_name', descKey: 'topbar.reverse_model_desc', color: 'text-emerald-400', bg: 'bg-emerald-500/10', borderColor: 'border-emerald-500/20' },
  { icon: Sparkles, labelKey: 'topbar.finetuning', descKey: 'topbar.finetuning_desc', color: 'text-amber-400', bg: 'bg-amber-500/10', borderColor: 'border-amber-500/20' },
  { icon: Shield, labelKey: 'topbar.malware_analysis', descKey: 'topbar.malware_analysis_desc', color: 'text-blue-400', bg: 'bg-blue-500/10', borderColor: 'border-blue-500/20' },
  { icon: Radar, labelKey: 'topbar.vuln_mining', descKey: 'topbar.vuln_mining_desc', color: 'text-indigo-400', bg: 'bg-indigo-500/10', borderColor: 'border-indigo-500/20' },
];

interface CaptchaData {
  id: string;
  question: string;
  svg?: string;
}

const getApiUrl = (path: string): string => {
  if (import.meta.env.VITE_API_URL && import.meta.env.PROD) {
    return `${import.meta.env.VITE_API_URL}${path}`;
  }
  return path;
};

const PasswordStrength = ({ password, confirmPassword }: { password: string; confirmPassword: string }) => {
  const { t } = useTranslation('auth');
  const passwordChecks = [
    { label: t('rules.min_chars'), met: password.length >= 8 },
    { label: t('rules.lowercase'), met: /[a-z]/.test(password) },
    { label: t('rules.uppercase'), met: /[A-Z]/.test(password) },
    { label: t('rules.number'), met: /[0-9]/.test(password) },
    { label: t('rules.symbol'), met: /[^A-Za-z0-9]/.test(password) },
  ];
  const matchCheck = { label: t('password_match'), met: !!password && password === confirmPassword };
  const checks = [...passwordChecks, matchCheck];
  const metCount = passwordChecks.filter((c) => c.met).length;
  const isValid = metCount >= 3 && matchCheck.met;

  return (
    <div className={`rounded-xl border p-3 transition-colors ${
      isValid ? 'border-emerald-500/25 bg-emerald-500/5' : 'border-border bg-muted/30'
    }`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">{t('reset_password_requirements')}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('password_requirements')}</p>
        </div>
        <span className="shrink-0 rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          {metCount}/5
        </span>
      </div>
      <div className="mt-3 grid grid-cols-5 gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-1.5 rounded-full transition-colors ${i < metCount ? 'bg-emerald-500' : 'bg-border'}`} />
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        {checks.map((check) => (
          <div
            key={check.label}
            className={`flex min-h-8 min-w-0 items-center gap-1.5 rounded-lg border px-2 text-xs transition-colors ${
              check.met
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600'
                : 'border-border bg-background/70 text-muted-foreground'
            }`}
          >
            {check.met ? (
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-current opacity-40" />
            )}
            <span className="truncate">{check.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ResetPassword() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [captcha, setCaptcha] = useState<CaptchaData | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [requestError, setRequestError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const timer = window.setTimeout(() => {
      setResendCooldown((current) => current - 1);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (step === 'email' && !captcha && !captchaLoading) {
      void loadCaptcha();
    }
  }, [step, captcha, captchaLoading]);

  const loadCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const response = await fetch(getApiUrl('/api/auth/captcha'), { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(getApiErrorMessage(data, t, 'network_error'));
      }
      setCaptcha(data);
      setCaptchaAnswer('');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('network_error'));
    } finally {
      setCaptchaLoading(false);
    }
  };

  const passwordMeetsRules = (password: string) => {
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    return score >= 3;
  };

  const handleSendCode = async () => {
    setRequestError('');
    if (!email.trim()) {
      setRequestError(t('email_required'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setRequestError(t('invalid_email'));
      return;
    }
    if (!captcha?.id || captchaAnswer.trim().length < 4) {
      if (!captchaLoading) {
        await loadCaptcha();
      }
      setRequestError(t('please_solve_puzzle'));
      return;
    }

    setSendingCode(true);
    setRequestError('');
    try {
      const response = await fetch(getApiUrl('/api/auth/request-password-reset'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          captchaId: captcha.id,
          captchaAnswer: captchaAnswer.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(getApiErrorMessage(data, t, 'failed_resend'));
      }
      toast.success(t('code_sent'));
      setStep('reset');
      setResendCooldown(60);
      setCaptchaAnswer('');
      setCaptcha(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('network_error');
      setRequestError(message);
      toast.error(message);
      await loadCaptcha();
    } finally {
      setSendingCode(false);
    }
  };

  const handleResetPassword = async () => {
    setRequestError('');
    if (verifyCode.length !== 6) {
      setRequestError(t('enter_code'));
      return;
    }
    if (!passwordMeetsRules(newPassword)) {
      setRequestError(t('password_not_meet'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setRequestError(t('password_no_match'));
      return;
    }

    setResetting(true);
    try {
      const response = await fetch(getApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          code: verifyCode,
          newPassword,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(getApiErrorMessage(data, t, 'an_error_occurred'));
      }
      toast.success(t('reset_password_success'));
      navigate('/auth');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('network_error');
      setRequestError(message);
      toast.error(message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="h-dvh overflow-hidden bg-background text-foreground relative">
      <PageTitle dynamicTitle={t('reset_password_title')} />

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.14),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.12),transparent_30%)] pointer-events-none" />

      <div className="relative grid h-full min-h-0 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="hidden h-full min-h-0 overflow-hidden lg:flex flex-col justify-between p-7 xl:p-8 border-r border-border/50 bg-card/30 backdrop-blur-sm">
          <div>
            <BackButton onClick={() => navigate('/auth')} label={t('common:go_back')} />
            <div className="mt-5 inline-flex items-center gap-3 rounded-2xl border border-border/60 bg-card/80 px-4 py-3 shadow-sm">
              <Logo />
              <div>
                <p className="text-sm font-semibold tracking-tight">Etherworks</p>
                <p className="text-xs text-muted-foreground">{t('reset_password_kicker')}</p>
              </div>
            </div>

            <div className="mt-6 max-w-xl space-y-3">
              <span className="inline-flex rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                {t('reset_password_badge')}
              </span>
              <h1 className="text-4xl xl:text-[2.8rem] font-semibold tracking-tight leading-tight">
                {t('reset_password_hero_title')}
              </h1>
              <p className="text-sm text-muted-foreground leading-6">
                {t('reset_password_hero_subtitle')}
              </p>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {productShowcase.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.labelKey} className={`rounded-2xl border ${item.borderColor} ${item.bg} p-3`}>
                    <div className={`inline-flex rounded-xl p-2 ${item.bg}`}>
                      <Icon className={`w-5 h-5 ${item.color}`} />
                    </div>
                    <p className="mt-2.5 text-sm font-medium text-foreground">{t(item.labelKey, { ns: 'common' })}</p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">{t(item.descKey, { ns: 'common' })}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            {t('reset_password_footer')}
          </p>
        </section>

        <section className="flex h-full min-h-0 items-center justify-center overflow-hidden p-4 sm:p-5 lg:p-6">
          <div className="flex w-full max-w-xl min-h-0 max-h-full flex-col overflow-hidden rounded-3xl border border-border/60 bg-card/90 p-5 sm:p-6 shadow-2xl shadow-black/5 backdrop-blur-xl">
            <div className="lg:hidden mb-6">
              <BackButton onClick={() => navigate('/auth')} label={t('common:go_back')} />
            </div>

            <div className="space-y-3">
              <div className="inline-flex rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                {t('reset_password_badge')}
              </div>
              <div>
                <h2 className="text-3xl font-semibold tracking-tight">
                  {t('reset_password_heading')}
                </h2>
                <p className="mt-1.5 text-sm text-muted-foreground leading-6">
                  {step === 'email'
                    ? t('reset_password_intro')
                    : t('reset_password_step_two_intro')}
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className={`rounded-2xl border p-3.5 ${step === 'email' ? 'border-primary/30 bg-primary/5' : 'border-border/60 bg-muted/30'}`}>
                <div className="flex items-center gap-3">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${step === 'email' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>1</div>
                  <div>
                    <p className="text-sm font-medium">{t('reset_step_email_title')}</p>
                    <p className="text-xs text-muted-foreground">{t('reset_step_email_desc')}</p>
                  </div>
                </div>
              </div>
              <div className={`rounded-2xl border p-3.5 ${step === 'reset' ? 'border-primary/30 bg-primary/5' : 'border-border/60 bg-muted/30'}`}>
                <div className="flex items-center gap-3">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${step === 'reset' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>2</div>
                  <div>
                    <p className="text-sm font-medium">{t('reset_step_password_title')}</p>
                    <p className="text-xs text-muted-foreground">{t('reset_step_password_desc')}</p>
                  </div>
                </div>
              </div>
            </div>

            {requestError && (
              <div className="mt-6 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {requestError}
              </div>
            )}

            {step === 'email' ? (
              <div className="mt-5 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">{t('email_address')}</label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder={t('email_placeholder')}
                      className="input h-12 w-full rounded-2xl pl-11"
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-border/60 bg-card/70 p-4 shadow-sm shadow-black/5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <ShieldCheck className="h-4.5 w-4.5" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{t('security_verification')}</p>
                        <p className="mt-1 text-xs text-muted-foreground leading-5">{t('reset_password_captcha_desc')}</p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={loadCaptcha}
                      disabled={captchaLoading}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/70 bg-background/80 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:border-primary/30 hover:bg-primary/5 disabled:text-muted-foreground"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${captchaLoading ? 'animate-spin' : ''}`} />
                      {t('new_puzzle')}
                    </button>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_11rem] sm:items-center">
                    <div className="space-y-2">
                      <label className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">{t('security_verification')}</label>
                      <input
                        type="text"
                        value={captchaAnswer}
                        onChange={(event) => setCaptchaAnswer(event.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 6))}
                        placeholder={t('please_solve_puzzle')}
                        className="input h-11 w-full rounded-2xl bg-background"
                      />
                      <p className="text-xs text-muted-foreground">{t('captcha_instruction')}</p>
                    </div>

                    <div className="rounded-2xl border border-border/60 bg-white/90 px-3 py-3 min-h-[84px] flex items-center justify-center overflow-hidden shadow-inner shadow-black/5">
                      {captchaLoading ? (
                        <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                      ) : captcha?.svg ? (
                        <div className="scale-[0.92] sm:scale-100" dangerouslySetInnerHTML={{ __html: captcha.svg }} />
                      ) : captcha?.question ? (
                        <span className="text-sm text-muted-foreground text-center">{captcha.question}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground text-center">{t('captcha_instruction')}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-border/60 bg-muted/40 p-3.5">
                  <p className="text-sm font-medium text-foreground">{t('reset_what_happens_next')}</p>
                  <ul className="mt-2.5 space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
                    <li>{t('reset_what_happens_next_one')}</li>
                    <li>{t('reset_what_happens_next_two')}</li>
                    <li>{t('reset_what_happens_next_three')}</li>
                  </ul>
                </div>

                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Link to="/auth" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                    {t('sign_in_instead')}
                  </Link>
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={sendingCode}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-50"
                  >
                    {sendingCode ? t('please_wait') : t('send_code')}
                    {!sendingCode && <ArrowRight className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-600">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{email}</span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm font-medium text-foreground">{t('enter_code')}</label>
                    <button
                      type="button"
                      onClick={() => {
                        setRequestError('');
                        setCaptchaAnswer('');
                        setStep('email');
                        void loadCaptcha();
                      }}
                      disabled={sendingCode || resendCooldown > 0}
                      className="inline-flex items-center gap-2 text-xs font-medium text-primary disabled:text-muted-foreground"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      {resendCooldown > 0 ? t('resend_in', { seconds: resendCooldown }) : t('resend_code')}
                    </button>
                  </div>
                  <input
                    type="text"
                    value={verifyCode}
                    onChange={(event) => setVerifyCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="input h-12 w-full rounded-2xl text-center text-xl tracking-[0.45em] font-semibold"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">{t('password')}</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    placeholder={t('password')}
                    className="input h-12 w-full rounded-2xl"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">{t('confirm_new_password')}</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder={t('confirm_new_password')}
                    className="input h-12 w-full rounded-2xl"
                  />
                </div>

                <PasswordStrength password={newPassword} confirmPassword={confirmPassword} />

                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setRequestError('');
                    }}
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t('change_email')}
                  </button>
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={resetting}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-50"
                  >
                    {resetting ? t('please_wait') : t('reset_password_button')}
                    {!resetting && <ArrowRight className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}